import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";
import { cookieUtils } from "../../../utils/cookieUtils";
import { ErrorToast } from "../../../components/toastMessage";



/**
 * unapproved user available pages routes.
 */
const WHITELIST_ROUTES = [
  "/account-verification-status",
  "/user-account-not-verified",
  "/launch-page",
  "/user-profile",
];

const NON_REGISTRATION_ROLES = [
  "System Admin",
  "Level 1",
  "Level 2",
  "Level 3"
];

/**
 * Props definition for the RoleGuard component.
 */
interface RoleGuardProps {
  allowedRoles: string | string[]; // Roles that are permitted to access the component
  children: React.ReactElement; // The component to be rendered if access is granted
}

/**
 * RoleGuard component restricts access to certain pages based on user roles.
 * - If a user is not verified, they are redirected to an account verification page.
 * - If a user does not have the required role, they are redirected to the login page.
 * - Whitelisted routes are always accessible.
 * - Plot Owners are automatically approved and have full access.
 *
 * @param {RoleGuardProps} props - The properties for the guard.
 * @returns {React.ReactElement} - The protected component if access is granted, otherwise a redirect.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ allowedRoles, children }) => {
  const location = useLocation();
  const user = useUser();

  console.log('=== ROLE GUARD TRIGGERED ===');
  console.log('Current path:', location.pathname);
  console.log('User from context:', user);

  // Normalize allowedRoles to always be an array
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  // Retrieve additional user details from BOTH sources
  const userRegistrationDetailsLocal = JSON.parse(
    localStorage.getItem("userRegistrationDetails") || "{}"
  ) as Record<string, any>;

  const userRegistrationDetailsCookie = cookieUtils.getSecureCookie("userRegistrationDetails");

  const userDetails = cookieUtils.getSecureCookie("userDetails");

  if (NON_REGISTRATION_ROLES.includes(userDetails?.role)) {
    // Only role-based check applies
    const userRole = userDetails?.role || "";
    const allowed = roles.length === 0 ||
      roles.map(r => r.toLowerCase()).includes(userRole.toLowerCase());

    return allowed ? children : <Navigate to="/onboarding/login" replace />;
  }

  console.log('=== USER DATA CHECK ===');
  console.log('From localStorage:', {
    exists: Object.keys(userRegistrationDetailsLocal).length > 0,
    user_type: userRegistrationDetailsLocal?.user_type,
    regestration_status: userRegistrationDetailsLocal?.regestration_status
  });
  console.log('From cookie:', {
    exists: !!userRegistrationDetailsCookie,
    user_type: userRegistrationDetailsCookie?.user_type,
    regestration_status: userRegistrationDetailsCookie?.regestration_status
  });
  console.log('User details cookie:', {
    exists: !!userDetails,
    role: userDetails?.role
  });

  // Use cookie data as priority, fallback to localStorage
  const userRegistrationDetails = userRegistrationDetailsCookie || userRegistrationDetailsLocal;
  const isRegistrationDataReady =
    !!userRegistrationDetails &&
    typeof userRegistrationDetails.regestration_status !== "undefined";

  console.log('Final userRegistrationDetails used:', {
    user_type: userRegistrationDetails?.user_type,
    regestration_status: userRegistrationDetails?.regestration_status
  });

  /**
   * Restrict unverified users from accessing non-whitelisted pages.
   * - If the current route is NOT in the whitelist and the user is "user" but not verified,
   *   they are redirected to the account verification page.
   * - 🔥 EXCEPTION: Plot Owners are auto-approved and always have access.
   */

  // 🔥 Check if user is Plot Owner (auto-approved)
  const isPlotOwner = userRegistrationDetails?.user_type === 'Plot Owner';
  const registrationStatus = userRegistrationDetails?.regestration_status;

  console.log('=== ACCESS DECISION ===');
  console.log('Is whitelisted route:', WHITELIST_ROUTES.includes(location.pathname));
  console.log('User role:', userDetails?.role);
  console.log('Registration status:', registrationStatus);
  console.log('Is Plot Owner:', isPlotOwner);

  // ⏳ Wait until registration data is ready
  if (!isRegistrationDataReady) {
    console.log("⏳ Registration data not ready yet, skipping redirect");
    return null; // or a loader
  }

  if (
    !WHITELIST_ROUTES.includes(location.pathname) &&
    userDetails?.role === "user" &&
    registrationStatus !== 1 &&
    !isPlotOwner
  ) {
    console.log('❌ ACCESS DENIED - Redirecting to not-verified');
    ErrorToast("Access denied");
    return <Navigate to="/user-account-not-verified" replace />;
  }

  console.log('✅ ACCESS GRANTED');

  /**
   * Allow access to all roles if no restrictions are set.
   * - If allowedRoles is an empty array, this route is accessible to everyone.
   */
  if (roles.length === 0) {
    console.log('No role restrictions - allowing access');
    return children;
  }

  /**
   * Restrict access to users whose roles do not match the allowed roles.
   * - If the user's role is not found in the allowed roles list, redirect to login.
   */

  const userRole = user?.role || "";

  if (!roles.map(r => r.toLowerCase()).includes(userRole.toLowerCase())) {
    console.log('❌ Role not allowed - Redirecting to login');
    return <Navigate to="/onboarding/login" replace />;
  }

  // if (!roles.includes(user?.role?.toLowerCase() || "")) {
  //   console.log('❌ Role not allowed - Redirecting to login');
  //   return <Navigate to="/onboarding/login" replace />;
  // }

  // If all checks pass, allow access to the component
  console.log('✅ All checks passed - rendering component');
  return children;
};

export default RoleGuard;